import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FieldConfigDTO, MoneyDTO } from '../../../../../../../../system/system-dtos';
import { MoneyService } from '../../../../../../../data-type-widgets/data-type/shared/money/money.service';
import { ManualAdjustmentModeConfig } from '../manual-adjustment-cash/manual-adjustment-cash.component';
import { CurrencyDTO } from '../../../../../../../data-type-widgets/data-type/lottomart/model/lottomart-model';
import { DiamondsSummary } from '../app-manual-adjustment-modal.component';
import { DiamondsBalanceDTO } from '../../manual-adjustment.model';

@Component({
  selector: 'app-manual-adjustment-diamonds',
  templateUrl: './manual-adjustment-diamonds.component.html',
  styleUrls: ['./manual-adjustment-diamonds.component.scss'],
  standalone: false,
})
export class ManualAdjustmentDiamondsComponent implements OnInit {
  private readonly EQUALS_ICON = 'fas fa-equals';

  private readonly UP_ICON = 'fas fa-angle-double-up';

  private readonly DOWN_ICON = 'fas fa-angle-double-down';

  @Output() deltaChanged: EventEmitter<DiamondsSummary> = new EventEmitter<DiamondsSummary>();

  @Input() current: DiamondsBalanceDTO;

  @Input() currency: CurrencyDTO;

  modeConfig: ManualAdjustmentModeConfig;

  currentWorthConfig: FieldConfigDTO;

  currentWorth: MoneyDTO;

  currentValue: number;

  postChangesWorthConfig: FieldConfigDTO;

  postChangesWorth: MoneyDTO;

  postChangesValue: number;

  deltaWorthConfig: FieldConfigDTO;

  deltaWorth: MoneyDTO;

  summaryIcon: string = this.EQUALS_ICON;

  summaryClass: string = '';

  formGroup: UntypedFormGroup;

  operation: string = '';

  constructor(
    private moneyService: MoneyService,
    private fb: UntypedFormBuilder
  ) {
    this.currentWorthConfig = this.moneyService.moneyConfig('currentWorth');
    this.postChangesWorthConfig = this.moneyService.moneyConfig('postChangesWorth');
    this.deltaWorthConfig = this.moneyService.moneyConfig('deltaWorth');
  }

  ngOnInit(): void {
    this.currentValue = Number(this.current.balance);
    this.currentWorth = this.createWorthObjectFromDiamonds(this.currentValue);

    this.postChangesWorth = this.createWorthObjectFromDiamonds(this.currentValue);
    this.postChangesValue = this.currentValue;

    this.deltaWorth = this.moneyService.zeroMoney(this.currency);

    this.formGroup = this.fb.group({
      checked: [false],
      adjustmentType: ['CREDIT'],
      delta: 0,
    });

    this.formGroup.valueChanges.subscribe((obj: { checked: boolean; delta: number; adjustmentType: string }) => {
      const delta = Number(obj.delta);
      if (isNaN(delta)) {
        this.formGroup.get('delta').setValue(0);
        return;
      }
      if (delta < 0) {
        this.formGroup.get('delta').setValue(delta * -1);
        return;
      }

      const deltaValueWithSign = this.getDeltaValueWithSign(delta, obj.adjustmentType);
      this.deltaWorth = this.createWorthObjectFromDiamonds(deltaValueWithSign);
      this.postChangesValue = this.currentValue + deltaValueWithSign;
      this.postChangesWorth = this.createWorthObjectFromDiamonds(this.postChangesValue);
      this.determineSummaryIconAndClass();

      if (obj.checked) {
        this.deltaChanged.emit({
          before: this.currentValue,
          beforeWorth: this.currentWorth,
          delta: deltaValueWithSign,
          deltaWorth: this.deltaWorth,
          after: this.postChangesValue,
          afterWorth: this.postChangesWorth,
        });
      } else {
        this.deltaChanged.emit(null);
      }
    });
  }

  private determineSummaryIconAndClass(): void {
    if (this.postChangesValue > this.currentValue) {
      this.summaryIcon = this.UP_ICON;
      this.summaryClass = 'delta-positive';
      this.operation = '(CREDIT)';
    } else if (this.postChangesValue < this.currentValue) {
      this.summaryIcon = this.DOWN_ICON;
      this.summaryClass = 'delta-negative';
      this.operation = '(DEBIT)';
    } else {
      this.summaryIcon = this.EQUALS_ICON;
      this.summaryClass = '';
    }
  }

  calculateWorth(value: number): number {
    return Math.floor(value / this.current.redeem);
  }

  createWorthObjectFromDiamonds(value: number): MoneyDTO {
    return this.moneyService.create(this.calculateWorth(value), this.currency);
  }

  private getDeltaValueWithSign(value: number, adjustmentType: string): number {
    return adjustmentType === 'CREDIT' ? value : -value;
  }
}
